import React, { useEffect, useState } from 'react';
import apiClient from '../../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Trash2, Edit2, Package, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const [form, setForm] = useState({
    name: '',
    pass_type: 'period',
    duration_days: 30,
    total_count: 10,
    price: 0,
    description: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await apiClient.get('/admin/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('상품 목록 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      pass_type: 'period',
      duration_days: 30,
      total_count: 10,
      price: 0,
      description: ''
    });
    setEditMode(false);
    setSelectedProduct(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setForm({
      name: product.name,
      pass_type: product.pass_type,
      duration_days: product.duration_days || 30,
      total_count: product.total_count || 10,
      price: product.price || 0,
      description: product.description || ''
    });
    setSelectedProduct(product);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name) {
      toast.error('상품명을 입력해주세요');
      return;
    }

    if (!form.duration_days) {
      toast.error('이용 기간(일수)을 입력해주세요');
      return;
    }

    if (form.pass_type === 'count' && !form.total_count) {
      toast.error('총 횟수를 입력해주세요');
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        name: form.name,
        pass_type: form.pass_type,
        duration_days: parseInt(form.duration_days),
        total_count: form.pass_type === 'count' ? parseInt(form.total_count) : null,
        price: parseInt(form.price) || 0,
        description: form.description
      };

      if (editMode && selectedProduct) {
        await apiClient.put(`/admin/products/${selectedProduct._id}`, payload);
        toast.success('상품이 수정되었습니다');
      } else {
        await apiClient.post('/admin/products', payload);
        toast.success('상품이 생성되었습니다');
      }

      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error('상품 저장 실패');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!productToDelete) return;

    try {
      await apiClient.delete(`/admin/products/${productToDelete._id}/permanent`);
      toast.success('상품이 삭제되었습니다');
      setDeleteDialogOpen(false);
      setProductToDelete(null);
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('상품 삭제 실패');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">상품 관리</h1>
          <p className="text-gray-500 mt-1">이용권 템플릿을 관리합니다</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openCreateDialog} className="bg-gray-900 hover:bg-gray-800">
            <Plus className="mr-2 h-4 w-4" />
            상품 생성
          </Button>
        </div>
      </div>

      {/* Product List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>등록된 상품이 없습니다.</p>
              <p className="text-sm mt-2">상품 생성 버튼을 눌러 이용권 템플릿을 추가하세요.</p>
            </CardContent>
          </Card>
        ) : (
          products.map((product) => (
            <Card key={product._id} className={`${!product.is_active ? 'opacity-50' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{product.name}</CardTitle>
                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded ${
                      product.pass_type === 'period' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {product.pass_type === 'period' ? '기간제' : '횟수제'}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(product)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        setProductToDelete(product);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {product.pass_type === 'period' ? (
                    <div className="flex justify-between">
                      <span className="text-gray-500">이용 기간</span>
                      <span className="font-medium">{product.duration_days}일</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-500">총 횟수</span>
                        <span className="font-medium">{product.total_count}회</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">이용 기간</span>
                        <span className="font-medium">{product.duration_days || '-'}일</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">가격</span>
                    <span className="font-medium">{formatPrice(product.price)}원</span>
                  </div>
                  {product.description && (
                    <p className="text-gray-500 text-xs mt-2 pt-2 border-t">
                      {product.description}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editMode ? '상품 수정' : '상품 생성'}</DialogTitle>
            <DialogDescription>
              이용권 템플릿 정보를 입력하세요.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>이용권 유형</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="pass_type"
                    value="period"
                    checked={form.pass_type === 'period'}
                    onChange={(e) => setForm({ ...form, pass_type: e.target.value })}
                    className="w-4 h-4"
                  />
                  <span>기간제</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="pass_type"
                    value="count"
                    checked={form.pass_type === 'count'}
                    onChange={(e) => setForm({ ...form, pass_type: e.target.value })}
                    className="w-4 h-4"
                  />
                  <span>횟수제</span>
                </label>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="name">상품명 *</Label>
              <Input
                id="name"
                placeholder={form.pass_type === 'period' ? '예: 1개월 무제한' : '예: 10회 이용권'}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            {form.pass_type === 'period' ? (
              <div className="grid gap-2">
                <Label htmlFor="duration">이용 기간 (일) *</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  placeholder="30"
                  value={form.duration_days}
                  onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                />
              </div>
            ) : (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="count">총 횟수 *</Label>
                  <Input
                    id="count"
                    type="number"
                    min="1"
                    placeholder="10"
                    value={form.total_count}
                    onChange={(e) => {
                      const count = parseInt(e.target.value) || 10;
                      // 기본 이용기간: 횟수 ÷ 2 × 7일 (주 2회 기준)
                      const defaultDuration = Math.ceil(count / 2) * 7;
                      setForm({ 
                        ...form, 
                        total_count: e.target.value,
                        duration_days: defaultDuration
                      });
                    }}
                  />
                  <p className="text-xs text-gray-500">기본 기간: 주 2회 소진 기준으로 자동 계산</p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="count_duration">이용 기간 (일) *</Label>
                  <Input
                    id="count_duration"
                    type="number"
                    min="1"
                    placeholder="35"
                    value={form.duration_days}
                    onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">
                    {form.total_count}회 기준 권장: {Math.ceil(parseInt(form.total_count || 10) / 2) * 7}일 ({Math.ceil(parseInt(form.total_count || 10) / 2)}주)
                  </p>
                </div>
              </>
            )}

            <div className="grid gap-2">
              <Label htmlFor="price">가격 (원)</Label>
              <Input
                id="price"
                type="number"
                min="0"
                placeholder="100000"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">설명</Label>
              <Textarea
                id="description"
                placeholder="상품 설명 (선택사항)"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              취소
            </Button>
            <Button onClick={handleSubmit} disabled={formLoading}>
              {formLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  저장 중...
                </>
              ) : (
                editMode ? '수정' : '생성'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>상품 삭제</DialogTitle>
            <DialogDescription>
              "{productToDelete?.name}" 상품을 삭제하시겠습니까?
              <br />
              삭제된 상품은 복구할 수 없습니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              취소
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
